var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var _AutomatorAppIntegrationSettingsForm_integrationId_accessor_storage;
// Import dependencies
import { html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import apiFetch from '@wordpress/api-fetch';
import { initStateManager } from '../../utils/state-manager';
import { collectFormData } from '../../utils/form-data-collector';
import { overrideFormValidation } from '../../utils/form-validator';
import { createSnackbar } from '@automator/shared/utils/create-snackbar';
// Import components
import { listenForEvent, dispatchEvent } from '../../utils/event-manager';
/**
 * Form wrapper component that handles REST API submissions, validation, and response handling
 * for app integration settings.
 *
 * This component provides a clean, modern way to handle form submissions in integration
 * settings pages. It integrates with WordPress REST API endpoints and provides comprehensive
 * feedback through alerts and notices.
 *
 * Features:
 * - REST API Integration with automatic OAuth redirect handling
 * - Alert system for snackbar notifications
 * - Notice system for inline panel alerts
 * - Automatic data updates from REST responses
 * - Loading state management
 * - Form validation integration
 * - State management integration
 *
 * @example
 * ```html
 * <uap-app-integration-settings-form integration-id="my-integration">
 *     <form method="POST">
 *         <?php wp_nonce_field( 'automator_integration_settings', 'automator_nonce' ); ?>
 *         <uap-app-integration-settings-button type="submit" name="automator_action" value="save_settings">
 *             Save Settings
 *         </uap-app-integration-settings-button>
 *     </form>
 * </uap-app-integration-settings-form>
 * ```
 *
 * @since 6.9.0 Initial implementation
 */
let AutomatorAppIntegrationSettingsForm = class AutomatorAppIntegrationSettingsForm extends LitElement {
    constructor() {
        super(...arguments);
        _AutomatorAppIntegrationSettingsForm_integrationId_accessor_storage.set(this, '');
        /**
         * Internal form element reference
         */
        this.form = null;
    }
    /**
     * Disable Shadow DOM to allow form elements to work properly
     *
     * @return {this} The render root
     */
    createRenderRoot() {
        return this;
    }
    /**
     * The integration ID for REST API calls
     *
     * This determines which class is used to process the form submission with the Action_Manager class.
     */
    get integrationId() { return __classPrivateFieldGet(this, _AutomatorAppIntegrationSettingsForm_integrationId_accessor_storage, "f"); }
    set integrationId(value) { __classPrivateFieldSet(this, _AutomatorAppIntegrationSettingsForm_integrationId_accessor_storage, value, "f"); }
    /**
     * Called after the first render
     *
     * @return {void}
     */
    firstUpdated() {
        // Find the form inside this component
        this.form = this.querySelector('form');
        if (this.form && this.integrationId) {
            // Initialize state management for this form
            initStateManager(this);
            // Set up form handling
            this.setupForm(this.form);
        }
    }
    /**
     * Set up form event listeners
     *
     * @param {HTMLFormElement} form - The form element to set up
     *
     * @return {void}
     */
    setupForm(form) {
        // Override form validation
        overrideFormValidation(form);
        // Listen for centralized events
        listenForEvent('button-click', this.handleFormButtonClick.bind(this));
        listenForEvent('button-error', this.handleFormButtonError.bind(this));
    }
    /**
     * Handle form button click events
     *
     * @param {CustomEvent<IntegrationSettingsEvent>} event - The button click event
     *
     * @return {void}
     */
    handleFormButtonClick(event) {
        if (!this.form) {
            return;
        }
        const { data, rowSubmission } = event.detail;
        // Type guard to ensure this is a button click event
        if (event.detail.action !== 'button-click') {
            return;
        }
        const buttonData = data;
        const { integrationId, postValues } = buttonData;
        // Use the overridden form validation
        if (!this.form.checkValidity()) {
            this.form.reportValidity();
            return;
        }
        // Collect form data.
        const formData = collectFormData(this.form, {
            // Exclude hidden fields.
            includeHidden: false,
            // Pass the row submission data if it exists.
            rowSubmission: rowSubmission
        });
        // Add event data if not already in form.
        const { name, value } = buttonData;
        if (name && !formData.has(name)) {
            formData.append(name, value || '');
        }
        // Merge additional postValues if available.
        if (postValues) {
            for (const [key, value] of Object.entries(postValues)) {
                (Array.isArray(value) ? value : [value]).forEach(val => formData.append(key, String(val)));
            }
        }
        // Submit to REST API
        this.submitToRest(formData, integrationId).catch(error => {
            console.error('Submission error:', error);
            this.showAlert({
                type: 'error',
                heading: 'Error',
                content: error.message || 'Action failed'
            });
        }).finally(() => {
            // Dispatch completion event to clear loading states
            dispatchEvent('submission-complete', { success: true }, rowSubmission);
        });
    }
    /**
     * Handle form button error events
     *
     * @param {CustomEvent<IntegrationSettingsEvent>} event - The button error event
     *
     * @return {void}
     */
    handleFormButtonError(event) {
        // Type guard to ensure this is a button error event
        if (event.detail.action !== 'button-error') {
            return;
        }
        const errorData = event.detail.data;
        this.showAlert({
            type: 'error',
            heading: 'Error',
            content: errorData.error.message || 'Button action failed'
        });
    }
    /**
     * Submit form data to REST API
     *
     * @param {FormData} formData - The form data to submit
     * @param {string} [integrationIdOverride] - Optional integration ID override
     *
     * @return {Promise<void>}
     */
    async submitToRest(formData, integrationIdOverride) {
        const action = formData.get('automator_action');
        if (!action) {
            throw new Error('No action specified');
        }
        // Use the integration ID override if provided, otherwise use the form's default
        const integrationId = integrationIdOverride || this.integrationId;
        const requestData = {
            action: action,
            data: {}
        };
        for (const [key, value] of formData.entries()) {
            if (key !== 'automator_action' && key !== 'automator_nonce') {
                requestData.data[key] = value;
            }
        }
        const result = await apiFetch({
            url: `/wp-json/uap/v2/integration-settings/${integrationId}`,
            method: 'POST',
            data: requestData
        });
        // Handle redirects
        if (result.redirect_url) {
            window.location.href = result.redirect_url;
            return;
        }
        // Handle reload
        if (result.reload) {
            window.location.reload();
            return;
        }
        // Handle data updates
        if (result.data && Array.isArray(result.data)) {
            this.handleDataUpdate(result.data);
        }
        // Handle alert objects (snackbar notification)
        if (result.alert) {
            this.showAlert(result.alert);
        }
        // Handle notice objects (uo-alert in panel content)
        if (result.notice) {
            this.showNotice(result.notice);
        }
    }
    /**
     * Handle data updates for table rows
     *
     * @param data - The updated data array
     */
    handleDataUpdate(data) {
        // Dispatch centralized data update event
        dispatchEvent('data-update', { data }, false);
    }
    /**
     * Show alert using snackbar (notification)
     *
     * @param {AlertObject} alert - The alert object to display
     *
     * @return {void}
     */
    showAlert(alert) {
        // Use snackbar for alerts - simpler and more appropriate for notifications
        createSnackbar({
            message: alert.content,
            color: alert.type === 'error' ? 'error' : '',
            iconID: this.getAlertIcon(alert.type),
            duration: 5000 // 5 seconds
        });
    }
    /**
     * Get appropriate icon for alert type
     *
     * @param {AlertObject['type']} type - The alert type
     *
     * @return {string} The icon ID to use
     */
    getAlertIcon(type) {
        switch (type) {
            case 'error':
                return 'xmark';
            case 'warning':
                return 'exclamation-triangle';
            case 'success':
                return 'check';
            case 'info':
                return 'info-circle';
            default:
                return 'info-circle';
        }
    }
    /**
     * Show notice using uo-alert component in panel content
     *
     * @param {NoticeObject} notice - The notice object to display
     *
     * @return {void}
     */
    showNotice(notice) {
        // Find the settings panel content area
        const panelContent = this.querySelector('.uap-settings-panel-content');
        if (!panelContent) {
            console.warn('No settings panel content found for notice insertion');
            return;
        }
        // Create a uo-alert element
        const alertElement = document.createElement('uo-alert');
        alertElement.setAttribute('type', notice.type);
        if (notice.heading) {
            alertElement.setAttribute('heading', notice.heading);
        }
        alertElement.textContent = notice.content;
        alertElement.className = 'uap-spacing-bottom';
        // Insert at the beginning of the panel content
        panelContent.insertBefore(alertElement, panelContent.firstChild);
        // Auto-remove after 5 seconds for success/info notices
        if (notice.type === 'success' || notice.type === 'info') {
            setTimeout(() => {
                if (alertElement.parentNode) {
                    alertElement.remove();
                }
            }, 5000);
        }
    }
    /**
     * Render the component
     *
     * @return {TemplateResult}
     */
    render() {
        return html `
			<slot></slot>
		`;
    }
};
_AutomatorAppIntegrationSettingsForm_integrationId_accessor_storage = new WeakMap();
__decorate([
    property({ type: String, attribute: 'integration-id' })
], AutomatorAppIntegrationSettingsForm.prototype, "integrationId", null);
AutomatorAppIntegrationSettingsForm = __decorate([
    customElement('uap-app-integration-settings-form')
], AutomatorAppIntegrationSettingsForm);
export { AutomatorAppIntegrationSettingsForm };
